import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  GeoJSON,
  Popup,
  Marker,
  useMap,
  Tooltip,
  ScaleControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./Mapview.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import Footer from "./footer"; 
import Menu from "./Menu";
import { FaEraser,  FaTimes, FaTrash } from "react-icons/fa";
import { createRoot } from "react-dom/client"; 
import MeasureControl from "./MeasureControl";
import DrawPolygonControl, { handlePointSearch, TitresDisplay } from "./DrawPolygonControl";
import CustomZoomControl from "./CustomZoomControl";
import { FaMale } from "react-icons/fa"; 
import ImportModal from "./ImportModal";
import ImportControl from "./ImportControl";
import DxfParser from 'dxf-parser';
import ZonageCotationsPanel from "./ZonageCotationsPanel";
import proj4 from 'proj4';

import jsPDF from "jspdf";
import { exportToDxf, collectMapDataForExport } from './dxfExport';
import UnifiedControlPanel from "./UnifiedControlPanel";



/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const { BaseLayer } = LayersControl;

// Définir les projections
proj4.defs('EPSG:4326', '+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees');
proj4.defs('EPSG:26191', '+proj=lcc +lat_1=33.3 +lat_0=33.3 +lon_0=-5.4 +k_0=0.999625769 +x_0=500000 +y_0=300000 +a=6378249.2 +b=6356515 +towgs84=31,146,47,0,0,0,0 +units=m +no_defs');

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const searchIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  iconSize: [20, 30],
  iconAnchor: [15, 42],
  popupAnchor: [0, -40],
});

///////////////////////////////////////////////////////////////////////////////////////

function extractCoordsFromGoogleMaps(url) {
  if (!url) return null;
  let regex, match;

  regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
  match = url.match(regex);

  if (!match) {
    regex = /search\/(-?\d+\.\d+),[+\s]*(-?\d+\.\d+)/;
    match = url.match(regex);
  }
  if (!match) {
    regex = /[?&]query=(-?\d+\.\d+),(-?\d+\.\d+)/;
    match = url.match(regex);
  }
  if (!match) {
    regex = /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/;
    match = url.match(regex);
  }
  if (match) {
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (isNaN(lat) || isNaN(lng)) return null;
    return { x: lng, y: lat };
  }
  return null;
}

///////////////////////////////////////////////////////////////////////////////////////////

function FitBounds({ geojsons, active = true, triggerFit = true }) {
  const map = useMap();
  
  useEffect(() => {
    if (!active || geojsons.length === 0 || !triggerFit) return;
    
    const group = geojsons.map((g) => JSON.parse(g.geom_geojson));
    const leafletLayer = L.geoJSON(group);
    
    const bounds = leafletLayer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [geojsons, map, active, triggerFit]); 

  return null;
}

////////////////////////////////////////////////////////////////////////////////////////////

function SearchPanel({
  search,
  setSearch,
  searchType,
  setSearchType,
  handleSearch,
  isLoading,
}) {

  return (
    <div className="search-panel-modern">
      <h2>Recherche Titres Fonciers</h2>
      <div className="search-controls-modern">
        <select
          className="search-select"
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
        >
          <option value="titref">Titre foncier</option>
          <option value="coord">Coordonnées</option>
          <option value="link">Lien Google Maps</option>
        </select>
        <div className="input-group-modern">
          <input
            type="text"
            placeholder={
              searchType === "titref"
                ? "Numéro de titre foncier"
                : searchType === "coord"
                ? "X,Y (EPSG:26191/4326)"
                : "Coller le lien Google Maps"
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          />
          <button onClick={handleSearch} disabled={isLoading}>
            {isLoading ? "Recherche..." : "Rechercher"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant pour afficher les coordonnées du curseur/////////////////////////////////////////////

function MouseCoordinates({ setMouseCoords }) {
  const map = useMap();

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { lat, lng } = e.latlng;
      setMouseCoords({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
    };

    map.on("mousemove", handleMouseMove);

    return () => {
      map.off("mousemove", handleMouseMove);
    };
  }, [map, setMouseCoords]);

  return null;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////

function EraserControl({ selectedLayers, setTitres, setSelectedLayers }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;


    const control = L.control({ position: "topright" });

    control.onAdd = function () {

      const frame = L.DomUtil.create("div", "leaflet-control leaflet-bar");
      frame.style.cssText = `
        background: rgba(255, 255, 255, 0.9);
        padding: 8px;
        border-radius: 10px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        flex-direction: column;
        align-items: center;
      `;

      const btnStyle = `
        cursor: pointer;
        background: white;
        border-radius: 8px;
        width: 34px;
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 5px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        transition: 0.2s;
      `;

      const btnEraser = L.DomUtil.create("div", "", frame);
      btnEraser.style.cssText = btnStyle;
      btnEraser.title = "Supprimer les titres sélectionnées";

      // 🔹 Icône moderne avec react-icons
      createRoot(btnEraser).render(<FaEraser size={18} color="#2980b9" />);

      // 🔹 Effet hover
      btnEraser.onmouseenter = () => {
        btnEraser.style.transform = "scale(1.1)";
        btnEraser.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
      };
      btnEraser.onmouseleave = () => {
        btnEraser.style.transform = "scale(1)";
        btnEraser.style.boxShadow = "0 2px 6px rgba(0,0,0,0.2)";
      };

      // 🔹 Action au clic
      L.DomEvent.on(btnEraser, "click", (e) => {
        L.DomEvent.stopPropagation(e);

        if (selectedLayers.length > 0) {
          setTitres((prev) => prev.filter((t) => !selectedLayers.includes(t.gid)));
          setSelectedLayers([]);
        } else {
          alert("Sélectionnez d'abord une ou plusieurs couches (Ctrl + clic).");
        }
      });

      return frame;
    };

    control.addTo(map);

    return () => control.remove();
  }, [map, selectedLayers, setTitres, setSelectedLayers]);

  return null;
}

// 🔹 Checkbox d'un zonage////////////////////////////////////////////////////////////////////////////

function ZonageCheckbox({ titreGid, zonage, isChecked, onToggle, onEyeClick }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "4px",
        padding: "2px 0",
        borderBottom: "1px solid #eee"
      }}
    >
  
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <strong>{zonage.type_zonage}</strong>
        <span>{Number(zonage.surface_intersection_totale_m2).toFixed(2)} m²</span>
        <span>{Number(zonage.pourcentage_intersection).toFixed(2)}%</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) =>
              onToggle(titreGid, zonage.zonage_gid, e.target.checked)
            }
          />
          Constructible
        </label>

        <button
          onClick={() => onEyeClick(zonage)}
          style={{
            cursor: "pointer",
            border: "none",
            background: "transparent",
            fontSize: "16px"
          }}
          title="Voir ce zonage sur la carte"
        >
          👁
        </button>
      </div>
    </div>
  );
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function LocateControl() {
  const map = useMap();
  const controlRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    if (!controlRef.current) {
      const container = L.DomUtil.create("div", "leaflet-control leaflet-bar");
      container.style.cssText = `
        background: rgba(255, 255, 255, 0.95);
        padding: 8px;
        border-radius: 10px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        display: flex;
        flex-direction: column;
        align-items: center;
        z-index: 1000;
        margin-top: -5px;
      `;

      const btn = L.DomUtil.create("div", "", container);
      btn.style.cssText = `
        cursor: pointer;
        background: white;
        border-radius: 8px;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        transition: 0.2s;
      `;
      btn.title = "Où suis-je ?";

      createRoot(btn).render(<FaMale color="#2980b9" size={18} />);

      // Hover effects
      btn.onmouseover = () => {
        btn.style.transform = "scale(1.1)";
        btn.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
        btn.style.background = "#e3f2fd";
      };
      btn.onmouseout = () => {
        btn.style.transform = "scale(1)";
        btn.style.boxShadow = "0 2px 6px rgba(0,0,0,0.2)";
        btn.style.background = "white";
      };

      // Action : localisation
      L.DomEvent.on(btn, "click", (e) => {
        L.DomEvent.stopPropagation(e);

        if (!navigator.geolocation) {
          alert("La géolocalisation n'est pas supportée.");
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;

            const circle = L.circle([latitude, longitude], {
              radius: 40,
              color: "#2980b9",
              weight: 2,
              fillColor: "#2980b9",
              fillOpacity: 0.2,
            }).addTo(map);

            const marker = L.circleMarker([latitude, longitude], {
              radius: 10,
              color: "#2570a1ff",
              fillColor: "#2980b9",
              fillOpacity: 1,
            }).addTo(map);

            map.fitBounds(circle.getBounds());
          },
          (err) => alert("Impossible de récupérer votre position : " + err.message)
        );
      });

      // Ajout du contrôle
      const ControlClass = L.Control.extend({
        options: { position: "topleft" },
        onAdd: () => container,
      });

      controlRef.current = new ControlClass();
      map.addControl(controlRef.current);
    }

    return () => {
      if (controlRef.current) {
        map.removeControl(controlRef.current);
        controlRef.current = null;
      }
    };
  }, [map]);

  return null;
}

/////////////////////////////////////////////////////////////////////////////////////////////////////////

export default function MapView() {
  const [titres, setTitres] = useState([]);
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("titref");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSearch, setShowSearch] = useState(true);
  const [searchPoint, setSearchPoint] = useState(null);
  const [selectedLayers, setSelectedLayers] = useState([]);

  const [mouseCoords, setMouseCoords] = useState(null);
  const [selectedZonage, setSelectedZonage] = useState(null);
  const [drawnPolygon, setDrawnPolygon] = useState(null);
  const [zonagesChecked, setZonagesChecked] = useState({});
  const [zonagesDetails, setZonagesDetails] = useState({});
  const [polygonesExclus, setPolygonesExclus] = useState({});
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [titresFromPolygonControl, setTitresFromPolygonControl] = useState([]);
  const [titlesToDelete, setTitlesToDelete] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);

  ////////////////////////////////////////////////////////////////////////////

  const [showImportModal, setShowImportModal] = useState(false);
  const [importedLayers, setImportedLayers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  ////////////////////////////////////////////////////////////////////////////

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [drawedLayers, setDrawedLayers] = useState([]);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [shouldFitBounds, setShouldFitBounds] = useState(false);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);

  /////////////////////////////////////////////////////////////////////////////

    const [selectedTitleType, setSelectedTitleType] = useState(null);

    const [selectedTitre, setSelectedTitre] = useState(null);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [panelPosition, setPanelPosition] = useState("right");
    const [currentZoom, setCurrentZoom] = useState(0);

    const deletedTitlesRef = useRef(new Set());
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);


    const MIN_ZOOM_LABEL = 16;
    let surfaceMin;

    if (currentZoom >= 18) {
      surfaceMin = 0;          // afficher tout
    } else if (currentZoom >= 17) {
      surfaceMin = 200;        // moyens + grands
    } else if (currentZoom >= 16) {
      surfaceMin = 500;        // grands seulement
    } else {
      surfaceMin = 9999999;    // aucun label
    }


  
  /////////////////////////////////////////////////////////////////////////////////
  
// Remplacer la fonction handleExport existante par :
const handleExport = (exportType) => {
  console.log(`🔄 Début exportation en format: ${exportType}`);
  
  // Récupérer TOUS les titres visibles sur la carte
  const mapData = collectMapDataForExport(
    titres, 
    titresFromPolygonControl, 
    importedLayers, 
    drawedLayers, 
    mapMarkers
  );
  
  console.log(`📊 Données à exporter: ${mapData.features.length} éléments`);
  
  if (mapData.features.length === 0) {
    alert("❌ Aucun titre affiché sur la carte à exporter");
    return;
  }

  try {
    if (exportType === "dxf") {
      exportToDxf(mapData, "titres_carte");
    } else if (exportType === "kml") {
      // Garder l'export KML existant
      exportToKml(mapData);
    }
  
  } catch (error) {
    console.error("❌ Erreur lors de l'exportation:", error);
    alert(`Erreur lors de l'exportation: ${error.message}`);
  }
};
///////////////////////////////////////////////////////////////////////////
const handleExportPDF = (titre, surfaceRestante) => {
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text("Fiche du Titre", 20, 20);

  doc.setFontSize(12);
  doc.text(`Numéro du TF : ${titre.titref || titre.graphic_de || titre.num || "Non défini"}`, 20, 40);
  if (titre.surf_adop) doc.text(`Surface : ${Number(titre.surf_adop).toFixed(2)} m²`, 20, 50);
  if (titre.pa_nom) doc.text(`PA : ${titre.pa_nom}`, 20, 60);
  
  // Ligne supprimée
  doc.text(`Surface constructible : ${surfaceRestante.toFixed(2)} m²`, 20, 70);

  // Ajuster la position de la section zonages
  let y = 70; // Décalage réduit après suppression

  if (titre.zonages && titre.zonages.length > 0) {
    doc.text("Zonages intersectés :", 20, y);
    titre.zonages.forEach((z) => {
      y += 10;
      doc.text(`- ${z.nom || "Zonage"}`, 25, y);
    });
  }

  doc.save(`Titre_${titre.gid}.pdf`);
};
   

  const toggleSearchPanel = useCallback(() => {
    setIsSearchPanelOpen(prev => !prev);
  }, []);


  const closeSearchPanel = useCallback(() => {
    setIsSearchPanelOpen(false);
  }, []);
  
  const [selectionConfig, setSelectionConfig] = useState({
    active: false,
    mode: null, 
    currentSide: 1,
    polygon: null,
    status: 'idle', 
    disablePopups: false
  });
  
  const [temporaryPoints, setTemporaryPoints] = useState([]);

  const [shouldFitImportedBounds, setShouldFitImportedBounds] = useState(false);

  const [pointSelectionCallback, setPointSelectionCallback] = useState(null);

  const [selectedPolygonFromZonagePanel, setSelectedPolygonFromZonagePanel] = useState(null);

////////////////////////////////////////////////////////////////////////////////////////////////


const handlePolygonSelectFromZonagePanel = useCallback((selectionData) => {
  console.log("🎯🚨🚨🚨 Sélection depuis ZonageCotationsPanel:", selectionData);
  console.log("📊 État actuel selectedPolygonFromZonagePanel:", selectedPolygonFromZonagePanel);
  
  if (selectionData && selectionData.isSelected) {
    console.log("✅ NOUVELLE SÉLECTION DÉTECTÉE");
    console.log("📌 Détails:", {
      zonageGid: selectionData.zonageGid,
      polygonIndex: selectionData.polygonIndex,
      hasPolygon: !!selectionData.polygon,
      polygonData: selectionData.polygon
    });
    
    const newSelection = {
      zonageGid: selectionData.zonageGid,
      polygonIndex: selectionData.polygonIndex,
      polygon: selectionData.polygon
    };
    
    setSelectedPolygonFromZonagePanel(newSelection);
    console.log("🔄 État mis à jour:", newSelection);
    
    // 🔥 ZOOM sur le polygone sélectionné
    if (selectionData.polygon && selectionData.polygon.geom_geojson) {
      try {
        console.log("🎯 Tentative de zoom sur le polygone...");
        const geometry = JSON.parse(selectionData.polygon.geom_geojson);
        console.log("📐 Géométrie parsée:", geometry);
        
        const leafletLayer = L.geoJSON(geometry);
        const bounds = leafletLayer.getBounds();
        console.log("🗺️ Bounds calculés:", bounds);
        
        if (bounds.isValid()) {
          const map = document.querySelector('.leaflet-container')._leaflet_map;
          if (map) {
            console.log("📍 Début du zoom...");
            map.flyToBounds(bounds, {
              padding: [50, 50],
              maxZoom: 18,
              duration: 1.5,
              easeLinearity: 0.25
            });
            console.log("✅ Zoom effectué");
          } else {
            console.error("❌ Carte non trouvée");
          }
        } else {
          console.error("❌ Bounds invalides");
        }
      } catch (error) {
        console.error("❌ Erreur lors du zoom sur le polygone:", error);
      }
    } else {
      console.error("❌ Données de géométrie manquantes");
    }
  } else {
    console.log("🗑️ Désélection du polygone");
    setSelectedPolygonFromZonagePanel(null);
  }
}, []);

// 🎯 FONCTION POUR OBTENIR LE STYLE DU POLYGONE BASÉ SUR LA SÉLECTION
const getZonagePolygonStyle = useCallback((feature, zonageGid, polygonIndex) => {
  const isSelected = selectedPolygonFromZonagePanel && 
                    selectedPolygonFromZonagePanel.zonageGid === zonageGid && 
                    selectedPolygonFromZonagePanel.polygonIndex === polygonIndex;
  
  console.log(`🎨 Style pour ${zonageGid}-${polygonIndex}: ${isSelected ? 'SELECTED' : 'NORMAL'}`);
  
  if (isSelected) {
    return {
      color: "#ff0000",
      weight: 5,              
      fillColor: "#ff0000",   
      fillOpacity: 0.4,        
      opacity: 1,         
      className: "selected-polygon-highlight"
    };
  }
  
  return {
    color: "#0066cc",
    weight: 2,
    fillOpacity: 0.3,
    fillColor: "#0066cc",
    opacity: 0.7,
  };
}, [selectedPolygonFromZonagePanel]);

const togglePopups = useCallback((disable = true) => {
  const popupPane = document.querySelector('.leaflet-popup-pane')
  const tooltipPane = document.querySelector('.leaflet-tooltip-pane');
  
  if (popupPane) {
    popupPane.style.pointerEvents = disable ? 'none' : 'auto';
    popupPane.style.opacity = disable ? '0' : '1';
  }
  
  if (tooltipPane) {
    tooltipPane.style.pointerEvents = disable ? 'none' : 'auto';
  }
}, []);

const selectionStateRef = useRef({
  active: false,
  points: [],
  currentSide: 1
});

const handleSelectModeChange = useCallback((config) => {
  console.log("🗺️ Mode sélection changé:", config);
  setSelectionConfig(config);
  selectionStateRef.current = { ...selectionStateRef.current, ...config };
  
  togglePopups(config.disablePopups);
  
  if (!config.active) {
    setTemporaryPoints([]);
    selectionStateRef.current.points = [];
  }
  
  const mapElement = document.querySelector('.leaflet-container');
  if (mapElement) {
    mapElement.style.cursor = config.active ? 'crosshair' : '';
  }
}, [togglePopups]);


// 🎯 FONCTION POUR ENREGISTRER LE CALLBACK DU PANEL
const handleSideSelected = useCallback((callback) => {
  console.log("🎯 Configuration du callback de sélection:", callback);
  setPointSelectionCallback(() => callback);
}, []);

// 🎯 GESTION SIMPLIFIÉE DES CLICS EN MODE SÉLECTION
const handleMapClickForSelection = useCallback((e) => {
  if (!selectionConfig.active || !selectionConfig.disablePopups) {
    return;
  }

  const { lat, lng } = e.latlng;
  const point = [lng, lat];
  
  console.log("📍 Clic sur la carte en mode sélection:", point, 
              "Status:", selectionConfig.status);

  if (pointSelectionCallback) {
    console.log("✅ Envoi du point au panel");
    pointSelectionCallback(point);
  }
}, [selectionConfig, pointSelectionCallback]);

////////////////////////////////////////////////////////////////////////////////////////////////////

// 🎯 EFFET POUR METTRE À JOUR LES POINTS TEMPORAIRES
useEffect(() => {
  if (selectionConfig.active && selectionConfig.status === 'selecting_first_point') {
    setTemporaryPoints([]);
  }
}, [selectionConfig.active, selectionConfig.status]);

function SelectionClickHandler({ onMapClick }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map) return;
    
    const handleClick = (e) => {
      onMapClick(e);
    };
    
    map.on('click', handleClick);
    
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);
  
  return null;
}

// 🎯 COMPOSANT POUR AFFICHER LES POINTS ET LIGNES DE SÉLECTION
function SelectionVisualization({ selectionConfig, currentPoints, selectedSides }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map || !selectionConfig.active) return;
    
    const markers = [];
    const lines = [];
    
    // Afficher les points temporaires en cours de sélection
    currentPoints.forEach((point, index) => {
      const marker = L.circleMarker([point[1], point[0]], {
        radius: 8,
        color: '#ff0000',
        weight: 2,
        fillColor: '#ff0000',
        fillOpacity: 0.8
      }).addTo(map);
      
      marker.bindTooltip(`Point ${index + 1}`, {
        permanent: false,
        direction: 'top'
      });
      
      markers.push(marker);
    });
    
    if (currentPoints.length === 2) {
      const line = L.polyline([
        [currentPoints[0][1], currentPoints[0][0]],
        [currentPoints[1][1], currentPoints[1][0]]
      ], {
        color: '#ff0000',
        weight: 3,
        dashArray: '5, 5',
        opacity: 0.8
      }).addTo(map);
      
      const distance = calculateDistanceInMeters(currentPoints[0], currentPoints[1]);
      line.bindTooltip(`Distance: ${distance.toFixed(2)} m`, {
        permanent: false,
        direction: 'center'
      });
      
      lines.push(line);
    }

    selectedSides.forEach((side, index) => {
      const line = L.polyline([
        [side.pointA[1], side.pointA[0]],
        [side.pointB[1], side.pointB[0]]
      ], {
        color: '#008000',
        weight: 4,
        opacity: 0.8
      }).addTo(map);
      
      line.bindTooltip(`Côté ${index + 1}: ${side.distance.toFixed(2)} m`, {
        permanent: false,
        direction: 'center'
      });
      
      lines.push(line);
      
      // Points des côtés sélectionnés
      [side.pointA, side.pointB].forEach((point, pointIndex) => {
        const marker = L.circleMarker([point[1], point[0]], {
          radius: 6,
          color: '#008000',
          weight: 2,
          fillColor: '#008000',
          fillOpacity: 0.8
        }).addTo(map);
        
        marker.bindTooltip(`Côté ${index + 1} - Point ${pointIndex + 1}`, {
          permanent: false,
          direction: 'top'
        });
        
        markers.push(marker);
      });
    });
    
    return () => {
      markers.forEach(marker => map.removeLayer(marker));
      lines.forEach(line => map.removeLayer(line));
    };
  }, [map, selectionConfig.active, currentPoints, selectedSides]);
  
  return null;
}


//////////////////////////////////////////////////////////////////////////////////////


// 🎯 COMPOSANT POUR AFFICHER LES POINTS TEMPORAIRES DE SÉLECTION
function TemporarySelectionPoints({ points }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map || points.length === 0) return;
    
    // Créer des marqueurs pour les points temporaires
    const markers = points.map((point, index) => {
      const marker = L.circleMarker([point[1], point[0]], {
        radius: 8,
        color: '#ff0000',
        weight: 2,
        fillColor: '#ff0000',
        fillOpacity: 0.8
      }).addTo(map);
      
      // Ajouter un numéro
      marker.bindTooltip(`${index + 1}`, {
        permanent: true,
        direction: 'center',
        className: 'selection-point-tooltip'
      });
      
      return marker;
    });
    
    // Nettoyer les marqueurs
    return () => {
      markers.forEach(marker => map.removeLayer(marker));
    };
  }, [map, points]);
  
  return null;
}

// 🎯 COMPOSANT POUR AFFICHER LA LIGNE TEMPORAIRE
function TemporarySelectionLine({ points }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map || points.length < 2) return;
    
    // Créer une ligne entre les deux points
    const line = L.polyline([
      [points[0][1], points[0][0]],
      [points[1][1], points[1][0]]
    ], {
      color: '#ff0000',
      weight: 3,
      dashArray: '5, 5',
      opacity: 0.8
    }).addTo(map);
    
    return () => {
      map.removeLayer(line);
    };
  }, [map, points]);
  
  return null;
}

// 🎯 CALCUL DE DISTANCE (identique à celui du panel)
const calculateDistanceInMeters = (point1, point2) => {
  try {
    const [lon1, lat1] = point1;
    const [lon2, lat2] = point2;
    
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  } catch (err) {
    console.error("❌ Erreur calcul distance:", err);
    return 0;
  }
};

useEffect(() => {
  const mapContainer = document.querySelector('.leaflet-container');
  if (!mapContainer) return;

  if (selectionConfig.active && selectionConfig.disablePopups) {
    // Mode sélection actif - désactiver complètement l'interactivité des polygones
    mapContainer.classList.add('selection-mode-active');
    console.log("🔴 Mode sélection activé - Interactivité des polygones désactivée");
  } else {
    // Mode normal - réactiver l'interactivité
    mapContainer.classList.remove('selection-mode-active');
    console.log("🟢 Mode normal - Interactivité des polygones activée");
  }

  return () => {
    mapContainer.classList.remove('selection-mode-active');
  };
}, [selectionConfig.active, selectionConfig.disablePopups]);

// 🎯 EFFET POUR SURVEILLER ET MAINTENIR LE MODE SÉLECTION
useEffect(() => {
  console.log("🎯 État de sélection mis à jour:", {
    active: selectionConfig.active,
    status: selectionConfig.status,
    disablePopups: selectionConfig.disablePopups
  });
}, [selectionConfig]);

// 🎯 EFFET POUR CHANGER LE CURSEUR EN MODE SÉLECTION
useEffect(() => {
  const mapElement = document.querySelector('.leaflet-container');
  if (mapElement) {
    if (selectionConfig.active && selectionConfig.disablePopups) {
      mapElement.style.cursor = 'crosshair';
      mapElement.classList.add('selection-mode-active');
    } else {
      mapElement.style.cursor = '';
      mapElement.classList.remove('selection-mode-active');
    }
  }
}, [selectionConfig.active, selectionConfig.disablePopups]);


const handleMapClick = async (e) => {
  // 🎯 SI MODE SÉLECTION MANUELLE ACTIF → GÉRER LA SÉLECTION DES POINTS
  if (selectionConfig.active && selectionConfig.disablePopups) {
    handleMapClickForSelection(e);
    return;
  }

 }
  const handleMapMove = useCallback(() => {
    if (shouldFitBounds) {
      setShouldFitBounds(false);
    }
  }, [shouldFitBounds]);

  useEffect(() => {
    const map = document.querySelector('.leaflet-container');
    if (map) {
      map.addEventListener('mousedown', handleMapMove);
      map.addEventListener('wheel', handleMapMove);
      map.addEventListener('touchstart', handleMapMove);
      
      return () => {
        map.removeEventListener('mousedown', handleMapMove);
        map.removeEventListener('wheel', handleMapMove);
        map.removeEventListener('touchstart', handleMapMove);
      };
    }
  }, [handleMapMove]);


 {/*//////////////////////////////////////////////////Importation des fichiers//////////////////////////////////////////////*/ }


// Import DXF direct dans le frontend
const handleDxfImport = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const dxfContent = e.target.result;
        
        const parser = new DxfParser();
        const parsedDxf = parser.parseSync(dxfContent);
        
        console.log("DXF parsé:", parsedDxf);
        
        // Convertir en GeoJSON avec conversion des coordonnées
        const geojson = convertDxfToGeoJSON(parsedDxf);
        
        // Ajouter à la carte
        setImportedLayers(prev => [...prev, {
          id: Date.now(),
          name: file.name,
          type: 'dxf',
          data: geojson
        }]);
        
        resolve(geojson);
        
      } catch (error) {
        console.error("Erreur lors du parsing DXF:", error);
        reject(error);
      }
    };
    
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

const handleKmlImport = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const kmlContent = e.target.result;
        console.log("📄 Contenu KML chargé");
        
        // Convertir KML en GeoJSON
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(kmlContent, 'text/xml');
        const geojson = kmlToGeoJSON(kmlDoc);
        
        if (geojson && geojson.features && geojson.features.length > 0) {
          console.log(`✅ KML converti: ${geojson.features.length} features`);
          resolve(geojson);
        } else {
          reject(new Error("Aucune donnée valide trouvée dans le fichier KML"));
        }
        
      } catch (error) {
        console.error("❌ Erreur lors du parsing KML:", error);
        reject(error);
      }
    };
    
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

const handleGeojsonImport = (file) => {
  const reader = new FileReader();
  
  reader.onload = (e) => {
    try {
      const geojson = JSON.parse(e.target.result);
      
      setImportedLayers(prev => [...prev, {
        id: Date.now(),
        name: file.name,
        type: 'geojson',
        data: geojson
      }]);
      
    } catch (error) {
      console.error("Erreur lors du parsing GeoJSON:", error);
      alert("Erreur lors de la lecture du fichier GeoJSON");
    }
  };
  
  reader.readAsText(file);
};
// Fonction de conversion DXF vers GeoJSON
const convertDxfToGeoJSON = (parsedDxf) => {
  const features = [];
  
  console.log("📊 DXF structure complète:", parsedDxf);
  console.log("📊 Entities:", parsedDxf.entities);
  console.log("📊 Blocks:", parsedDxf.blocks);
  
  // Essayer d'extraire les entités des blocs d'abord
  if (parsedDxf.blocks) {
    Object.entries(parsedDxf.blocks).forEach(([blockName, block]) => {
      console.log(`🔍 Bloc ${blockName}:`, block.entities);
      if (block.entities && block.entities.length > 0) {
        block.entities.forEach(entity => {
          const feature = convertEntityToFeature(entity);
          if (feature) features.push(feature);
        });
      }
    });
  }

  if (parsedDxf.entities && parsedDxf.entities.length > 0) {
    parsedDxf.entities.forEach(entity => {
      const feature = convertEntityToFeature(entity);
      if (feature) features.push(feature);
    });
  }
  
  console.log(`🎯 Total features: ${features.length}`);
  return {
    type: 'FeatureCollection',
    features: features
  };
};


const convertEntityToFeature = (entity) => {
  console.log(`🔄 Conversion entité:`, entity);
  
  const properties = {
    layer: entity.layer || '0',
    color: entity.color || 0,
    entityType: entity.type
  };

  let geometry = null;

  const convertToDegrees = (x, y) => {
    try {
      if (x === undefined || y === undefined || isNaN(x) || isNaN(y)) {
        console.warn('❌ Coordonnées invalides:', x, y);
        return [0, 0];
      }
      
      const [lng, lat] = proj4('EPSG:26191', 'EPSG:4326', [x, y]);
      console.log(`📍 Conversion: [${x}, ${y}] -> [${lng}, ${lat}]`);
      return [lng, lat];
    } catch (error) {
      console.error('❌ Erreur conversion:', error);
      return [x, y];
    }
  };

  switch (entity.type) {
    case 'LWPOLYLINE':
      console.log("📐 Conversion LWPOLYLINE", entity);
      
      let coordinates = [];
      
      if (entity.vertices && entity.vertices.length > 0) {
        coordinates = entity.vertices.map(vertex => 
          convertToDegrees(vertex.x, vertex.y)
        );
      }
      
      console.log(`📍 Nombre de points: ${coordinates.length}`, coordinates);
      
      if (coordinates.length < 2) {
        console.warn("❌ Pas assez de points pour former une géométrie");
        return null;
      }
      

      let isClosed = entity.closed;
      
      // Si entity.closed est undefined, vérifier les flags ou la configuration
      if (isClosed === undefined) {
        // Méthode 1: Vérifier si le premier et dernier point sont identiques
        const firstPoint = coordinates[0];
        const lastPoint = coordinates[coordinates.length - 1];
        const isAutoClosed = (firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1]);
        
        // Méthode 2: Vérifier s'il y a un flag shape (vu dans votre console)
        if (entity.shape) {
          isClosed = true;
          console.log("✅ Fermeture détectée via entity.shape");
        } else if (isAutoClosed) {
          isClosed = true;
          console.log("✅ Fermeture détectée via points identiques");
        } else {
          // Forcer la fermeture si on sait que c'est un polygone (basé sur le contexte)
          isClosed = true;
          console.log("🔄 Forçage de la fermeture (polygone détecté)");
        }
      }
      
      console.log(`🔒 Polyligne fermée: ${isClosed}`);
      
      if (isClosed && coordinates.length >= 3) {
        // S'assurer que le polygone est vraiment fermé
        if (coordinates.length > 0 && 
            (coordinates[0][0] !== coordinates[coordinates.length-1][0] || 
             coordinates[0][1] !== coordinates[coordinates.length-1][1])) {
          coordinates.push([coordinates[0][0], coordinates[0][1]]);
          console.log("✅ Point de fermeture ajouté");
        }
        
        geometry = {
          type: 'Polygon',
          coordinates: [coordinates]
        };
        properties.entityType = 'POLYGON';
        console.log("✅ Converti en POLYGONE");
      } else {
        geometry = {
          type: 'LineString',
          coordinates: coordinates
        };
        console.log("✅ Converti en LINESTRING");
      }
      break;

    default:
      console.log(`❓ Type non géré: ${entity.type}`);
      return null;
  }

  if (!geometry) {
    console.log("❌ Aucune géométrie générée");
    return null;
  }

  return {
    type: 'Feature',
    properties: properties,
    geometry: geometry
  };
};

// Fonction simple pour convertir KML en GeoJSON
const kmlToGeoJSON = (kmlDoc) => {
  // Cette fonction est basique - pour une conversion plus robuste, utilisez une bibliothèque comme tokml
  const features = [];

  const placemarks = kmlDoc.getElementsByTagName('Placemark');
  
  Array.from(placemarks).forEach((placemark, index) => {
    const nameElement = placemark.getElementsByTagName('name')[0];
    const name = nameElement ? nameElement.textContent : `KML Feature ${index + 1}`;
    
    const coordinatesElements = placemark.getElementsByTagName('coordinates');
    
    Array.from(coordinatesElements).forEach((coordElement) => {
      const coordText = coordElement.textContent.trim();
      const coordPairs = coordText.split(' ').filter(coord => coord.trim() !== '');
      
      const coordinates = coordPairs.map(pair => {
        const [lng, lat, z] = pair.split(',').map(Number);
        return [lng, lat];
      });
      
      if (coordinates.length > 0) {
        let geometry;
        
        if (coordinates.length === 1) {
          geometry = {
            type: 'Point',
            coordinates: coordinates[0]
          };
        } else if (coordinates.length > 1 && coordinates[0][0] === coordinates[coordinates.length-1][0] && coordinates[0][1] === coordinates[coordinates.length-1][1]) {
          geometry = {
            type: 'Polygon',
            coordinates: [coordinates]
          };
        } else {
          geometry = {
            type: 'LineString',
            coordinates: coordinates
          };
        }
        
        features.push({
          type: 'Feature',
          properties: { name },
          geometry: geometry
        });
      }
    });
  });
  
  return {
    type: 'FeatureCollection',
    features: features
  };
};


// Ajuste automatique de la vue sur les GeoJSON importés
// Fonction d'ajustement de la vue pour les layers importés (KML, DXF, GeoJSON)
function FitImportedBounds({ importedLayers, shouldFitBounds, setShouldFitBounds }) {
  const map = useMap();
  
  useEffect(() => {
    if (importedLayers.length > 0 && shouldFitBounds) {
      // Prendre le dernier layer importé
      const latestLayer = importedLayers[importedLayers.length - 1];
      
      if (latestLayer.data && latestLayer.data.features.length > 0) {
        try {
          const leafletLayer = L.geoJSON(latestLayer.data);
          const bounds = leafletLayer.getBounds();
          
          if (bounds.isValid()) {
            console.log("🗺️ Zoom sur le fichier importé - Bounds:", bounds);
            map.fitBounds(bounds, { 
              padding: [50, 50],
              maxZoom: 18
            });
            
            // Réinitialiser l'état après le zoom
            setShouldFitBounds(false);
          } else {
            console.log("❌ Bounds invalides pour le fichier importé");
            setShouldFitBounds(false);
          }
        } catch (error) {
          console.error("❌ Erreur lors du fitBounds:", error);
          setShouldFitBounds(false);
        }
      } else {
        setShouldFitBounds(false);
      }
    }
  }, [importedLayers, map, shouldFitBounds, setShouldFitBounds]);
  
  return null;
}








// Exportation KML
const exportToKml = (geoJSONData) => {
  let kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Titres de la carte</name>
    <description>Export des titres et parcelles</description>`;
  
  // Styles pour différents types d'éléments
  kmlContent += `
    <Style id="titreStyle">
      <LineStyle>
        <color>ff0000ff</color>
        <width>3</width>
      </LineStyle>
      <PolyStyle>
        <color>400000ff</color>
      </PolyStyle>
    </Style>
    <Style id="batimentStyle">
      <LineStyle>
        <color>ff00ff00</color>
        <width>2</width>
      </LineStyle>
      <PolyStyle>
        <color>4000ff00</color>
      </PolyStyle>
    </Style>
    <Style id="texteStyle">
      <IconStyle>
        <scale>0</scale>
      </IconStyle>
    </Style>`;
  
  geoJSONData.features.forEach((feature, index) => {
    kmlContent += convertGeoJSONToKml(feature, index);
  });
  
  kmlContent += `
  </Document>
</kml>`;
  
  downloadFile(kmlContent, `${Date.now()}.kml`, 'application/vnd.google-earth.kml+xml');
};

// Déterminer la couche DXF selon le type de feature
const getLayerForFeature = (feature, layers) => {
  const props = feature.properties || {};
  
  if (props.type === 'titre') return layers.titres;
  if (props.type === 'batiment') return layers.batiments;
  if (props.type === 'texte') return layers.textes;
  if (props.source === 'import') return layers.import;
  
  return layers.titres;
};


const convertGeoJSONToKml = (feature, index) => {
  const { geometry, properties } = feature;
  let kmlContent = '';
  
  const name = properties.nom || properties.numero || properties.text || `Element_${index + 1}`;
  const description = buildDescription(properties);
  const style = getStyleForFeature(properties);

  kmlContent += `
    <Placemark>
      <name>${escapeXml(name)}</name>
      <description>${escapeXml(description)}</description>
      <styleUrl>#${style}</styleUrl>`;

  switch (geometry.type) {
    case 'Point':
      kmlContent += `
      <Point>
        <coordinates>${geometry.coordinates[0]},${geometry.coordinates[1]},0</coordinates>
      </Point>`;
      break;

    case 'LineString':
      kmlContent += `
      <LineString>
        <coordinates>${geometry.coordinates.map(coord => `${coord[0]},${coord[1]},0`).join(' ')}</coordinates>
      </LineString>`;
      break;

    case 'Polygon':
      geometry.coordinates.forEach((ring, ringIndex) => {
        kmlContent += `
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${ring.map(coord => `${coord[0]},${coord[1]},0`).join(' ')}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>`;
      });
      break;

    case 'MultiPolygon':
      geometry.coordinates.forEach(polygon => {
        polygon.forEach(ring => {
          kmlContent += `
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>${ring.map(coord => `${coord[0]},${coord[1]},0`).join(' ')}</coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>`;
        });
      });
      break;
  }

  kmlContent += `
    </Placemark>`;

  return kmlContent;
};

const generateDxfFooter = () => {
  return `0
  ENDSEC
  0
  EOF
  `;
};

const calculatePolygonCenter = (coordinates) => {
  if (coordinates.length === 0) return [0, 0];
  
  let sumX = 0;
  let sumY = 0;
  
  coordinates.forEach(coord => {
    sumX += coord[0];
    sumY += coord[1];
  });
  
  return [sumX / coordinates.length, sumY / coordinates.length];
};

const buildDescription = (properties) => {
  const desc = [];
  if (properties.nom) desc.push(`Nom: ${properties.nom}`);
  if (properties.numero) desc.push(`Numéro: ${properties.numero}`);
  if (properties.surface) desc.push(`Surface: ${properties.surface}`);
  if (properties.type) desc.push(`Type: ${properties.type}`);
  if (properties.source) desc.push(`Source: ${properties.source}`);
  
  return desc.join('\n');
};

const getStyleForFeature = (properties) => {
  if (properties.type === 'batiment') return 'batimentStyle';
  if (properties.type === 'texte') return 'texteStyle';
  return 'titreStyle';
};

const downloadFile = (content, fileName, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeXml = (unsafe) => {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
};

 {/*///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////*/ }


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const getAllDisplayedTitres = useCallback(() => {
  const normalTitres = [...titres, ...titresFromPolygonControl];
  
  // Ajouter les titres importés avec les données de la base de données
  const importedTitres = importedLayers.flatMap(layer => 
    layer.data.features.map((feature, index) => {
      // Récupérer les données de la base de données si disponibles
      const dbData = {
        titref: feature.properties?.titref_correct,
        graphic_de: feature.properties?.graphic_de_correct,
        num: feature.properties?.num_correct,
        surf_adop: feature.properties?.surf_adop_correct,
        pa_nom: feature.properties?.pa_nom_correct,
        gid: feature.properties?.gid_correct
      };

      // Déterminer si on a des données de la base de données
      const hasDbData = dbData.titref || dbData.graphic_de || dbData.num;

      // Utiliser les données de la base si disponibles, sinon les données du fichier
      return {
        gid: `imported-${layer.id}-${index}`,
        titref: dbData.titref || feature.properties?.titref || feature.properties?.numero || feature.properties?.name || `Importé ${index + 1}`,
        graphic_de: dbData.graphic_de || feature.properties?.graphic_de || feature.properties?.nom || `Importé ${index + 1}`,
        num: dbData.num || feature.properties?.num || `IMP-${index + 1}`,
        surf_adop: dbData.surf_adop || feature.properties?.surf_adop || feature.properties?.surface,
        pa_nom: dbData.pa_nom || feature.properties?.pa_nom || feature.properties?.commune || "Non défini",
        geom_geojson: JSON.stringify(feature.geometry),
        zonages: [], 
        isImported: true, 
        layerId: layer.id,
        featureIndex: index,
        layerName: layer.name,
        // Nouveaux champs pour indiquer la source des données
        fromDatabase: hasDbData,
        databaseData: hasDbData ? dbData : null,
        fileData: {
          titref: feature.properties?.titref,
          graphic_de: feature.properties?.graphic_de,
          num: feature.properties?.num,
          surf_adop: feature.properties?.surf_adop,
          pa_nom: feature.properties?.pa_nom
        },
        matchStatus: feature.properties?.match_found ? 'found' : 
                    feature.properties?.match_details ? 'not_found' : 'not_searched'
      };
    })
  );

  return [...normalTitres, ...importedTitres];
}, [titres, titresFromPolygonControl, importedLayers]);
//////////////////////////////////////////////////////////////////////////////////////////////////


const handleDeleteTitres = useCallback((titlesToDelete) => {
  console.log("🔴 Suppression des titres:", titlesToDelete);
  
  if (titlesToDelete.length === 0) {
    console.log("❌ Aucun titre à supprimer");
    return;
  }

  // Séparer les IDs des titres normaux et des titres importés
  const normalTitlesToDelete = titlesToDelete.filter(id => typeof id === 'number');
  const importedTitlesToDelete = titlesToDelete.filter(id => typeof id === 'string' && id.startsWith('imported-'));

  console.log("🗑️ Titres normaux à supprimer:", normalTitlesToDelete);
  console.log("🗑️ Titres importés à supprimer:", importedTitlesToDelete);

  // 1. Supprimer des titres de recherche (titre/coordonnées/lien)
  setTitres(prev => {
    const filtered = prev.filter(t => !normalTitlesToDelete.includes(t.gid));
    console.log(`🗑️ Titres recherche: ${prev.length} -> ${filtered.length}`);
    return filtered;
  });
  
  // 2. Supprimer des titres de DrawPolygonControl (point/polygone)
  setTitresFromPolygonControl(prev => {
    const filtered = prev.filter(t => !normalTitlesToDelete.includes(t.gid));
    console.log(`🗑️ Titres polygone: ${prev.length} -> ${filtered.length}`);
    return filtered;
  });
  
  // 3. Supprimer les titres importés
  if (importedTitlesToDelete.length > 0) {
    setImportedLayers(prev => {
      const updatedLayers = prev.map(layer => {
        // Extraire les INDEX de features à supprimer pour cette couche
        const featureIndexesToDelete = importedTitlesToDelete
          .filter(titreId => titreId.startsWith(`imported-${layer.id}-`))
          .map(titreId => {
            const parts = titreId.split('-');
            return parseInt(parts[parts.length - 1]);
          })
          .filter(index => !isNaN(index));

        console.log(`🔍 Couche "${layer.name}" - Index à supprimer:`, featureIndexesToDelete);

        if (featureIndexesToDelete.length === 0) {
          return layer;
        }

   
        const filteredFeatures = layer.data.features.filter((feature, index) => {
          const shouldKeep = !featureIndexesToDelete.includes(index);
          return shouldKeep;
        });

        console.log(`📊 Couche "${layer.name}": ${layer.data.features.length} -> ${filteredFeatures.length} features`);

        return {
          ...layer,
          data: {
            ...layer.data,
            features: filteredFeatures
          }
        };
      }).filter(layer => layer.data.features.length > 0);

      console.log(`🗑️ Couches importées: ${prev.length} -> ${updatedLayers.length}`);
      return updatedLayers;
    });
  }

  setSelectedLayers(prev => {
    const filtered = prev.filter(id => !titlesToDelete.includes(id));
    console.log(`🗑️ Layers sélectionnés: ${prev.length} -> ${filtered.length}`);
    return filtered;
  });

  setZonagesChecked(prev => {
    const newChecked = { ...prev };
    titlesToDelete.forEach(titreGid => {
      Object.keys(newChecked).forEach(key => {
        if (key.startsWith(`${titreGid}-`)) {
          delete newChecked[key];
        }
      });
    });
    return newChecked;
  });

  setZonagesDetails(prev => {
    const newDetails = { ...prev };
    titlesToDelete.forEach(titreGid => {
      Object.keys(newDetails).forEach(key => {
        if (key.includes(titreGid.toString())) {
          delete newDetails[key];
        }
      });
    });
    return newDetails;
  });

  setPolygonesExclus(prev => {
    const newExclus = { ...prev };
    titlesToDelete.forEach(titreGid => {
      Object.keys(newExclus).forEach(key => {
        if (key.startsWith(`${titreGid}-`)) {
          delete newExclus[key];
        }
      });
    });
    return newExclus;
  });

  if (selectedZonage) {
    const selectedTitreGid = selectedZonage.zonage_gid?.split('-')[1];
    if (selectedTitreGid && titlesToDelete.includes(parseInt(selectedTitreGid))) {
      setSelectedZonage(null);
    }
  }

  setDeleteMode(false);
  setTitlesToDelete([]);
  
  console.log("✅ Suppression terminée");
}, [selectedZonage]);


  const handlePolygonClickInDeleteMode = useCallback((e, titreGid) => {
    const isCtrlPressed = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
    
    if (isCtrlPressed) {

      setTitlesToDelete(prev => 
        prev.includes(titreGid) 
          ? prev.filter(id => id !== titreGid)
          : [...prev, titreGid]
      );
    } else {

      setTitlesToDelete(prev => 
        prev.includes(titreGid) 
          ? prev.filter(id => id !== titreGid)
          : [titreGid]
      );
    }
  }, []);
  

  useEffect(() => {
    const initialChecked = {};
    titres.forEach((titre) => {
      titre.zonages?.forEach((z) => {
        const key = `${titre.gid}-${z.zonage_gid}`;
        initialChecked[key] = true; 
      });
    });
    setZonagesChecked(initialChecked);
  }, [titres]);

///////////////////////////////Recuperer les regions////////////////////////////////////////////////

  useEffect(() => {
  fetch("https://api.qunciland.com/api/region")
    .then((res) => res.json())
    .then((data) => setRegions(data))
    .catch((err) => console.error("Erreur récupération régions :", err));
}, []);

/////////////////////////////////Recuperer les provinces/////////////////////////////////////////////

useEffect(() => {
  fetch("https://api.qunciland.com/api/province")
    .then(res => res.json())
     .then((data) => {
      console.log("✅ Provinces récupérées :", data);
      setProvinces(data);
    })
    
    .catch(err => console.error("Erreur récupération provinces :", err));
}, []);

///////////////////////////////Recuperer les communes///////////////////////////////////////////////

useEffect(() => {
  fetch("https://api.qunciland.com/api/commune")
    .then((res) => res.json())
    .then((data) => {
      console.log("✅ Communes récupérées :", data);
      setCommunes(data);
    })
    .catch((err) => console.error("❌ Erreur communes :", err));
}, []);

//////////////////////////////////////////////////////////////////////////////////////////////


const handleZonageToggle = (titreGid, zonageGid, isChecked) => {
  console.log("🎯 handleZonageToggle EXÉCUTÉ:", { titreGid, zonageGid, isChecked });
  
  const zonageKey = `${titreGid}-${zonageGid}`;
  setZonagesChecked(prev => {
    console.log("📊 État avant:", prev);
    console.log("📊 État après:", { ...prev, [zonageKey]: isChecked });
    return {
      ...prev,
      [zonageKey]: isChecked,
    };
  });
};


////////////////////////////////////////////////////////////////////////////////////////////////////

// Fonction pour extraire un point représentatif d'une géométrie
const getRepresentativePoint = (geometry) => {
  if (!geometry || !geometry.coordinates) return null;

  try {
    switch (geometry.type) {
      case 'Point':
        return geometry.coordinates; // [lng, lat]
      
      case 'LineString':
        // Premier point de la ligne
        return geometry.coordinates[0];
      
      case 'Polygon':
        // Premier point du premier ring (exterieur)
        return geometry.coordinates[0][0];
      
      case 'MultiPolygon':
        // Premier point du premier ring du premier polygone
        return geometry.coordinates[0][0][0];
      
      case 'MultiLineString':
        // Premier point de la première ligne
        return geometry.coordinates[0][0];
      
      case 'MultiPoint':
        // Premier point
        return geometry.coordinates[0];
      
      default:
        console.warn(`❌ Type de géométrie non supporté: ${geometry.type}`);
        return null;
    }
  } catch (error) {
    console.error("❌ Erreur extraction point représentatif:", error);
    return null;
  }
};
// Dans MapView.js - Fonction pour rechercher par coordonnées
const searchTitleByCoordinates = async (x, y) => {
  try {
    console.log(`📍 Recherche titre aux coordonnées: x=${x}, y=${y}`);
    
    const response = await fetch(
      `https://api.qunciland.com/api/titres/par-coordonnees?x=${x}&y=${y}`
    );
    
    if (!response.ok) {
      if (response.status === 404) {
        console.log("❌ Aucun titre trouvé à ces coordonnées");
        return null;
      }
      const errorText = await response.text();
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    console.log(`✅ Titre trouvé:`, result.titref);
    return result;

  } catch (error) {
    console.error(`❌ Erreur recherche par coordonnées:`, error);
    return null;
  }
};

// Modifiez la fonction exportToDxf locale (vers la ligne 500) pour accepter deux paramètres


const handleFileSelect = async (file, fileType) => {
  console.log("Fichier sélectionné:", file.name, "Type:", fileType);
  
  let geojsonData;
  
  if (fileType === "dxf") {
    geojsonData = await handleDxfImport(file);
  } else if (fileType === "kml") {
    geojsonData = await handleKmlImport(file);
  } else if (fileType === "geojson") {
    geojsonData = await handleGeojsonImport(file);
  }

  if (geojsonData && geojsonData.features) {
    console.log(`📁 ${geojsonData.features.length} features importées`);
    
    // 🔥 RECHERCHE AUTOMATIQUE PAR COORDONNÉES POUR CHAQUE FEATURE
    const enhancedFeatures = await Promise.all(
      geojsonData.features.map(async (feature, index) => {
        try {
          // Extraire le centroïde ou un point représentatif de la géométrie
          const representativePoint = getRepresentativePoint(feature.geometry);
          
          if (!representativePoint) {
            console.log(`❌ Impossible d'extraire un point pour la feature ${index + 1}`);
            return {
              ...feature,
              properties: {
                ...feature.properties,
                match_found: false,
                match_details: "Impossible d'extraire les coordonnées"
              }
            };
          }

          const [x, y] = representativePoint;
          console.log(`📍 Feature ${index + 1} - Point représentatif:`, { x, y });

          // Rechercher le titre à ces coordonnées
          const matchedTitle = await searchTitleByCoordinates(x, y);
          
          if (matchedTitle) {
            console.log(`✅ Titre correspondant trouvé:`, matchedTitle.titref);
            return {
              ...feature,
              properties: {
                ...feature.properties,
                // Ajouter les informations de la base de données
                titref_correct: matchedTitle.titref,
                graphic_de_correct: matchedTitle.graphic_de,
                num_correct: matchedTitle.num,
                surf_adop_correct: matchedTitle.surf_adop,
                pa_nom_correct: matchedTitle.pa_nom,
                gid_correct: matchedTitle.gid,
                // Coordonnées utilisées pour la recherche
                search_coordinates: { x, y },
                // Marquer comme correspondance trouvée
                match_found: true,
                match_details: `Correspondance trouvée: ${matchedTitle.titref}`
              }
            };
          } else {
            console.log(`❌ Aucun titre trouvé aux coordonnées: ${x}, ${y}`);
            return {
              ...feature,
              properties: {
                ...feature.properties,
                search_coordinates: { x, y },
                match_found: false,
                match_details: "Aucun titre trouvé à cet emplacement"
              }
            };
          }
        } catch (error) {
          console.error(`❌ Erreur sur la feature ${index + 1}:`, error);
          return {
            ...feature,
            properties: {
              ...feature.properties,
              match_found: false,
              match_details: `Erreur: ${error.message}`
            }
          };
        }
      })
    );

    // Mettre à jour les données avec les features enrichies
    const enhancedGeoJSON = {
      ...geojsonData,
      features: enhancedFeatures
    };

    // Statistiques
    const matches = enhancedFeatures.filter(f => f.properties.match_found).length;
    console.log(`📊 Résultats: ${matches}/${enhancedFeatures.length} correspondances`);

    // Activer le zoom
    setShouldFitImportedBounds(true);

    // Ajouter la couche importée
    setImportedLayers(prev => [...prev, {
      id: Date.now(),
      name: file.name,
      type: fileType,
      data: enhancedGeoJSON,
      matchStats: {
        total: enhancedFeatures.length,
        matches: matches,
        percentage: ((matches / enhancedFeatures.length) * 100).toFixed(1)
      }
    }]);
    
    setShowImportModal(false);

    // Afficher résumé
    if (matches > 0) {
     
    } else {

    }
  }
};


// Fonction pour charger les détails d'un zonage (polygones individuels)///////////////////////////

  const chargerDetailsZonage = async (titreGid, zonage) => {
    try {
      const res = await fetch(`https://api.qunciland.com/api/titres/${titreGid}/zonages/${zonage.zonage_gid}`);
      const details = await res.json();
      
      setZonagesDetails(prev => ({
        ...prev,
        [zonage.zonage_gid]: details
      }));
    } catch (err) {
      console.error("Erreur chargement détails zonage:", err);
    }
  };
  
////////////////////////////////////////////////////////////////////////////////////////////////////

// Modification de  fetchTitres pour ne pas toujours activer le fitBounds
  const fetchTitres = useCallback(async (query = "", type = "titref") => {
    setIsLoading(true);
    setError(null);
    try {
      let url = "https://api.qunciland.com/api/titres";
      if (query) {
        if (type === "coord" || type === "link") {
          const [x, y] = query.split(",").map((s) => s.trim());
          url += `?x=${x}&y=${y}`;
          setSearchPoint({ x: parseFloat(x), y: parseFloat(y) });
        } else {
          url += `?${type}=${encodeURIComponent(query)}`;
          setSearchPoint(null);
        }
      }

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Erreur HTTP: ${res.status}`);
      const data = await res.json();
      setTitres(data);
      if (data.length === 0)
        setError("Aucun titre foncier trouvé pour cette recherche.");
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

///////////////////////////////////////////////////////////////////////////////////////////




/////////////////////////////////////////////////////////////////////////////////////////////////////

    const handleSearch = () => {
    if (!search.trim()) return;

    setShouldFitBounds(true);

    if (searchType === "link") {
      const coords = extractCoordsFromGoogleMaps(search);
      if (!coords) {
        alert("Impossible d'extraire les coordonnées du lien Google Maps.");
        return;
      }
      fetchTitres(`${coords.x},${coords.y}`, "link");
    } else {
      fetchTitres(search, searchType);
    }
  };

  // Gestion du clic sur l'œil d'un zonage//////////////////////////////////////////////////
  const handleEyeClick = (zonage) => {
  if (!zonage || !zonage.zonage_gid) return; 
  setSelectedZonage(zonage);
    
  if (!zonagesDetails[zonage.zonage_gid]) {
    const titreGid = zonage.zonage_gid.split('-').pop();
    chargerDetailsZonage(titreGid, zonage);
  }
};

////////////////////////////////////////////////////////////////////////////////////////////
  
const handleExcludePolygon = useCallback((titreGid, zonageGid, polygoneIndex, surface) => {
  const polygoneKey = `${titreGid}-${zonageGid}-${polygoneIndex}`;
  
  setPolygonesExclus(prev => ({
    ...prev,
    [polygoneKey]: {
      excluded: true,
      surface: surface
    }
  }));
}, []);

  useEffect(() => {
    fetchTitres();
  }, [fetchTitres]);

  // 🔹 Recherche automatique par polygone dessiné ///////////////////////////////////////////////

  useEffect(() => {
    if (!drawnPolygon) return;
    const geojsonStr = JSON.stringify(drawnPolygon.geometry);
    console.log("Polygone envoyé au serveur :", geojsonStr);

    fetch(`https://api.qunciland.com/api/titres-in-polygon`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: geojsonStr,
    })
    
      .then((res) => res.json())
      .then((data) => {
    console.log("Titres avec zonages:", data); 
    setTitres(data);
  })
      .catch((err) => console.error(err));
  }, [drawnPolygon]);

  ///////////////////////////////////////////////////////////////////////////////////////////////

// Fonction de conversion améliorée
const convertCoordinatesToMeters = (coordinates, geometryType) => {
  try {
    console.log(`🔁 Conversion ${geometryType}:`, coordinates);
    
    if (geometryType === 'Point') {
      const [lng, lat] = coordinates;
      const [x, y] = proj4('EPSG:4326', 'EPSG:26191', [lng, lat]);
      console.log(`📍 Point converti: [${lng}, ${lat}] -> [${x}, ${y}]`);
      return [x, y];
    }
    
    if (geometryType === 'LineString') {
      return coordinates.map(coord => {
        const [lng, lat] = coord;
        const [x, y] = proj4('EPSG:4326', 'EPSG:26191', [lng, lat]);
        return [x, y];
      });
    }
    
    if (geometryType === 'Polygon') {
      return coordinates.map(ring => 
        ring.map(coord => {
          const [lng, lat] = coord;
          const [x, y] = proj4('EPSG:4326', 'EPSG:26191', [lng, lat]);
          return [x, y];
        })
      );
    }
    
    if (geometryType === 'MultiPolygon') {
      return coordinates.map(polygon => 
        polygon.map(ring => 
          ring.map(coord => {
            const [lng, lat] = coord;
            const [x, y] = proj4('EPSG:4326', 'EPSG:26191', [lng, lat]);
            return [x, y];
          })
        )
      );
    }
    
    return coordinates;
  } catch (error) {
    console.error('❌ Erreur conversion coordonnées:', error);
    return coordinates;
  }
};



// Fonction convertGeoJSONToDxf corrigée pour utiliser les coordonnées converties
const convertGeoJSONToDxf = (feature, layerName, index) => {
  const { geometry, properties } = feature;
  let dxfContent = '';

  console.log(`🔄 Conversion ${geometry.type} pour layer ${layerName}`, geometry.coordinates);

  switch (geometry.type) {
    case 'Point':
      const [x, y] = geometry.coordinates;
      dxfContent += `0
POINT
8
${layerName}
10
${x}
20
${y}
`;

  
      if (properties.nom || properties.text) {
        const text = properties.nom || properties.text || `Point_${index + 1}`;
        dxfContent += `0
TEXT
8
TEXTES
10
${x + 10}
20
${y + 10}
40
5
1
${text}
`;
      }
      break;

    case 'LineString':
      if (geometry.coordinates.length >= 2) {
        dxfContent += `0
LWPOLYLINE
8
${layerName}
90
${geometry.coordinates.length}
70
0
`;
        geometry.coordinates.forEach(coord => {
          const [x, y] = coord;
          dxfContent += `10
${x}
20
${y}
`;
        });

        if (properties.nom) {
          const firstCoord = geometry.coordinates[0];
          const [x, y] = firstCoord;
          dxfContent += `0
TEXT
8
TEXTES
10
${x}
20
${y}
40
3
1
${properties.nom}
`;
        }
      }
      break;

    case 'Polygon':
      if (geometry.coordinates.length > 0 && geometry.coordinates[0].length > 0) {
        const exteriorRing = geometry.coordinates[0];
        
        if (exteriorRing.length >= 3) {
          dxfContent += `0
LWPOLYLINE
8
${layerName}
90
${exteriorRing.length}
70
1
`;
          exteriorRing.forEach(coord => {
            const [x, y] = coord;
            dxfContent += `10
${x}
20
${y}
`;
          });
          
          // Ajouter un label au centre du polygone
          if (properties.nom || properties.numero) {
            const center = calculatePolygonCenter(exteriorRing);
            const [x, y] = center;
            const label = properties.nom || properties.numero || `Parcelle_${index + 1}`;
            dxfContent += `0
TEXT
8
TEXTES
10
${x}
20
${y}
40
4
1
${label}
`;
          }
        }
      }
      break;

    case 'MultiPolygon':
      geometry.coordinates.forEach((polygon, polyIndex) => {
        if (polygon.length > 0 && polygon[0].length >= 3) {
          const exteriorRing = polygon[0];
          dxfContent += `0
LWPOLYLINE
8
${layerName}
90
${exteriorRing.length}
70
1
`;
          exteriorRing.forEach(coord => {
            const [x, y] = coord;
            dxfContent += `10
${x}
20
${y}
`;
          });
        }
      });
      break;

    default:
      console.warn(`❌ Type de géométrie non supporté: ${geometry.type}`);
  }

  return dxfContent;
};

// Header DXF amélioré
const generateDxfHeader = () => {
  return `0
SECTION
2
HEADER
9
$ACADVER
1
AC1015
9
$INSUNITS
70
6
9
$INSBASE
10
0.0
20
0.0
30
0.0
9
$EXTMIN
10
0.0
20
0.0
9
$EXTMAX
10
1000000.0
20
1000000.0
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LAYER
70
6
0
LAYER
2
TITRES
70
0
62
1
6
CONTINUOUS
0
LAYER
2
BATIMENTS
70
0
62
3
6
CONTINUOUS
0
LAYER
2
TEXTES
70
0
62
5
6
CONTINUOUS
0
LAYER
2
IMPORT
70
0
62
2
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
`;
};

///////////////////////////////////////////////////////////////////////////////////////////





///////////////////////////////////////////////////////////////////////////////////////////





  return (
    
    <div className="map-container">
       <Menu />

      {/* 🆕 Panneau de recherche conditionnel */}
      {isSearchPanelOpen && (
        <SearchPanel
          search={search}
          setSearch={setSearch}
          searchType={searchType}
          setSearchType={setSearchType}
          handleSearch={handleSearch}
          isLoading={isLoading}
          onClose={closeSearchPanel}
        />
      )}

      

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <MapContainer
        center={[30, -7.5]}
        zoom={5}
        zoomControl={false}
        maxZoom={40}  
        style={{ height: "100%", width: "100%" }}
   
      >

        

       <SelectionClickHandler onMapClick={(e) => {

        if (selectionConfig.active && selectionConfig.disablePopups) {
          handleMapClickForSelection(e);
        } else {

          handleMapClick(e);
        }
      }} />
        <CustomZoomControl />

        <ImportControl onImportClick={() => setShowImportModal(true)} />

       

        <LayersControl position="bottomleft">
          <BaseLayer checked name="Google Satellite">
          <TileLayer
            url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
            attribution="&copy; Google Satellite, © CartoDB © OpenStreetMap"
            maxZoom={100}  
          />
        </BaseLayer>

          <BaseLayer name="OpenStreetMap">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              maxZoom={100}  
            />
          </BaseLayer>
          <BaseLayer name="Topographie">
            <TileLayer
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors'
              maxZoom={100}  
            />
          </BaseLayer>

          {/* 🔹 Couche regions */}          
          <LayersControl.Overlay name="Régions" >
            {regions.length > 0 && (
              <GeoJSON
                key="regions"
                data={{
                  type: "FeatureCollection",
                  features: regions.map((region) => ({
                    type: "Feature",
                    geometry: JSON.parse(region.geom_geojson),
                    properties: { name: region.name },
                  })),
                }}
                style={{
                  color: "#1e90ff",
                  weight: 2,
                  fillOpacity: 0.1,
                  fillColor: "#1e90ff",
                }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties?.name) {
                    layer.bindPopup(`<strong>Région :</strong> ${feature.properties.name}`);
                  }

                  // 🔹 Zoom sur le polygone au clic
                  layer.on("click", () => {
                    const bounds = layer.getBounds();
                    layer._map.fitBounds(bounds, { padding: [30, 30] });
                  });
                }}
              />
            )}
          </LayersControl.Overlay>

          {/* 🔹 Couche Provinces */}
          <LayersControl.Overlay name="Provinces" >
            {provinces.length > 0 && (
              <GeoJSON
                key="provinces"
                data={{
                  type: "FeatureCollection",
                  features: provinces.map((p) => ({
                    type: "Feature",
                    geometry: JSON.parse(p.geom_geojson),
                    properties: { name: p.name },
                  })),
                }}
                style={{
                  color: "#ff6347",
                  weight: 2,
                  fillOpacity: 0.3,
                  fillColor: "#ff8974ff",
                }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties?.name) {
                    layer.bindPopup(`<strong>Province :</strong> ${feature.properties.name}`);
                  }

                  // 🔹 Zoom sur le polygone au clic
                  layer.on("click", () => {
                    const bounds = layer.getBounds();
                    layer._map.fitBounds(bounds, { padding: [30, 30] });
                  });
                }}
              />
            )}
          </LayersControl.Overlay>

          {/* 🔹 Couche Communes */}
          <LayersControl.Overlay name="Communes" >
            {communes.length > 0 && (
              <GeoJSON
                key="communes"
                data={{
                  type: "FeatureCollection",
                  features: communes.map((commune) => ({
                    type: "Feature",
                    geometry: JSON.parse(commune.geom_geojson),
                    properties: { name: commune.name },
                  })),
                }}
                style={{
                  color: "#124a12ff",
                  weight: 1.9,
                  fillOpacity: 0.3,
                  fillColor: "#b0dfb0ff",
                }}
                onEachFeature={(feature, layer) => {
                  if (feature.properties?.name) {
                    layer.bindPopup(`<strong>Commune :</strong> ${feature.properties.name}`);
                  }

                  // 🔹 Zoom sur le polygone au clic
                  layer.on("click", () => {
                    const bounds = layer.getBounds();
                    layer._map.fitBounds(bounds, { padding: [30, 30] });
                  });
                }}
              />
            )}
          </LayersControl.Overlay>
        </LayersControl>

  
        <FitBounds 
          geojsons={getAllDisplayedTitres()} 
          triggerFit={shouldFitBounds}
        />

         <FitImportedBounds 
          importedLayers={importedLayers} 
          shouldFitBounds={shouldFitImportedBounds}
          setShouldFitBounds={setShouldFitImportedBounds}
        />


        {/* AFFICHAGE DES TITRES DE RECHERCHE */}
        {titres.map((titre) => {
          // Calcul surface restante constructible AVEC EXCLUSIONS
          const surfaceRestante = titre.surf_adop -
            (titre.zonages?.reduce((sum, z) => {
              const zonageKey = `${titre.gid}-${z.zonage_gid}`;
              const isConstructible = zonagesChecked[zonageKey];
              
              // Surface de base du zonage
              let surfaceNonConstructible = isConstructible ? 0 : Number(z.surface_intersection_totale_m2 || 0);
              
              // Ajouter les surfaces des polygones individuels exclus
              if (zonagesDetails[z.zonage_gid]?.polygones_individualises) {
                zonagesDetails[z.zonage_gid].polygones_individualises.forEach((polygone, idx) => {
                  const polygoneKey = `${titre.gid}-${z.zonage_gid}-${idx}`;
                  if (polygonesExclus[polygoneKey]?.excluded) {
                    surfaceNonConstructible += polygonesExclus[polygoneKey].surface;
                  }
                });
              }
              return sum + surfaceNonConstructible;
            }, 0) || 0);

            const titreLabel =
              titre.titref || titre.graphic_de || titre.num || "Non défini";

            const surfaceLabel = titre.surf_adop
              ? `${Number(titre.surf_adop).toFixed(2)} m²`
              : "Surface inconnue";

            const fullLabel = `${titreLabel} - ${surfaceLabel}`;

          return (
            <GeoJSON
              key={titre.gid}
              data={JSON.parse(titre.geom_geojson)}
              interactive={!selectionConfig.active} 
              style={{
                color: titlesToDelete.includes(titre.gid) ? "#dc3545" : "orange",
                weight: titlesToDelete.includes(titre.gid) ? 4 : 2,
                fillOpacity: titlesToDelete.includes(titre.gid) ? 0.6 : 0.3,
                fillColor: titlesToDelete.includes(titre.gid) ? "#dc3545" : "orange",
              }}
    
                eventHandlers={{
                  click: (e) => {
                    if (deleteMode) {
                      e.originalEvent.stopPropagation();
                      e.target.closePopup();
                      handlePolygonClickInDeleteMode(e, titre.gid);
                    } else {
                      const isCtrlPressed = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
                      if (isCtrlPressed) {
                        // Ctrl+clic pour sélection multiple
                        setSelectedLayers(prev => {
                          if (prev.includes(titre.gid)) {
                            return prev.filter(id => id !== titre.gid);
                          } else {
                            return [...prev, titre.gid];
                          }
                        });
                      } else {
                        // Clic simple pour ouvrir la fenêtre latérale
                        e.originalEvent.stopPropagation();
                        setSelectedTitre(titre);
                        setSidePanelOpen(true);
                        // Optionnel: centrer la carte sur le titre
                        const bounds = e.target.getBounds();
                        if (bounds.isValid()) {
                          const map = document.querySelector('.leaflet-container')._leaflet_map;
                          map.flyToBounds(bounds, { padding: [100, 100], maxZoom: 18 });
                        }
                      }
                    }
                  },
                  mouseover: (e) => {
                    e.target.setStyle({ 
                      weight: 4, 
                      fillOpacity: 0.6 
                    });
                  },
                  mouseout: (e) => {
                    e.target.setStyle({
                      weight: titlesToDelete.includes(titre.gid) ? 4 : 2,
                      fillOpacity: titlesToDelete.includes(titre.gid) ? 0.6 : 0.3,
                      color: titlesToDelete.includes(titre.gid) ? "#dc3545" : "orange",
                      fillColor: titlesToDelete.includes(titre.gid) ? "#dc3545" : "orange",
                    });
                  },
                }}
            >
              
              {!deleteMode && (
                <Popup>
                  <div className="popup-modern">
                    {/* En-tête */}
                    <div className="popup-header">
                      <h3>{titre.titref || titre.graphic_de || titre.num || "Non défini"}</h3>
                    </div>
                    <div className="popup-section">
                      {titre.surf_adop && (
                        <p>
                          <strong>Surface :</strong>{" "}
                          {Number(titre.surf_adop).toFixed(2)} m²
                        </p>
                      )}
                      {titre.pa_nom ? (
                        <p><strong>PA :</strong> {titre.pa_nom}</p>
                      ) : (
                        <p className="popup-error">❌ Aucun PA trouvé</p>
                      )}
                    </div>

                    <div className="popup-section">
                      <h4>🟦 Zonages intersectés</h4>
                      {titre.zonages && titre.zonages.length > 0 ? (
                        <div className="popup-zonages">
                          {titre.zonages.map((z) => {
                            const zonageKey = `${titre.gid}-${z.zonage_gid}`;
                            const isChecked = zonagesChecked[zonageKey];
                            return (
                              <ZonageCheckbox
                                key={zonageKey}
                                titreGid={titre.gid}
                                zonage={z}
                                isChecked={isChecked}
                                onToggle={handleZonageToggle}
                                onEyeClick={handleEyeClick}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <p className="popup-error">❌ Aucun zonage trouvé</p>
                      )}
                    </div>
                    <div className="popup-highlight">
                      <strong>Surface constructible :</strong>{" "}
                      {surfaceRestante.toFixed(2)} m²
                    </div>
                     <button
                  onClick={() => handleExportPDF(titre, surfaceRestante)}
                  style={{
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    padding: "5px 10px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                >
                  Exporter en PDF
                </button>
                  </div>
                </Popup>
              )}
              {currentZoom >= 16 &&
               
                <Tooltip
                  permanent
                  direction="center"
                  className="titre-label"
                  opacity={1}
                >
                  <div>
                    <strong>{titreLabel}</strong>
                    <br />
                    {surfaceLabel}
                  </div>
                </Tooltip>
              }
            </GeoJSON>
          );
        })}
        
            {importedLayers.map(layer => 
              layer.data.features.map((feature, index) => {
                const titreId = `imported-${layer.id}-${index}`;
                const displayInfo = {
                  titref: feature.properties?.titref_correct || feature.properties?.titref || feature.properties?.numero || feature.properties?.name || `Importé ${index + 1}`,
                  graphic_de: feature.properties?.graphic_de_correct || feature.properties?.graphic_de || feature.properties?.nom || `Importé ${index + 1}`,
                  surf_adop: feature.properties?.surf_adop_correct || feature.properties?.surf_adop || feature.properties?.surface,
                  pa_nom: feature.properties?.pa_nom_correct || feature.properties?.pa_nom || feature.properties?.commune || "Non défini",
                  num: feature.properties?.num_correct || feature.properties?.num,
                  gid: feature.properties?.gid_correct || feature.properties?.gid
                };

                return (
                  <GeoJSON
                    key={titreId}
                    data={feature.geometry}
                    interactive={!selectionConfig.active} 
                    style={{
                      color: titlesToDelete.includes(titreId) ? "#dc3545" : "#3388ff",
                      weight: titlesToDelete.includes(titreId) ? 4 : 2,
                      fillOpacity: titlesToDelete.includes(titreId) ? 0.6 : 0.2,
                      fillColor: titlesToDelete.includes(titreId) ? "#dc3545" : "#3388ff",
                    }}
                    eventHandlers={{
                      click: (e) => {
                        if (deleteMode) {
                          e.originalEvent.stopPropagation();
                          e.target.closePopup();
                          handlePolygonClickInDeleteMode(e, titreId);
                        } else {
                          const isCtrlPressed = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
                          setSelectedLayers(prev => {
                            if (isCtrlPressed) {
                              if (prev.includes(titreId)) {
                                return prev.filter(id => id !== titreId);
                              } else {
                                return [...prev, titreId];
                              }
                            } else {
                              return [titreId];
                            }
                          });
                        }
                      },
                      mouseover: (e) => {
                        if (!deleteMode) {
                          e.target.setStyle({ 
                            weight: 4, 
                            fillOpacity: 0.4 
                          });
                        }
                      },
                      mouseout: (e) => {
                        if (!deleteMode) {
                          e.target.setStyle({
                            weight: titlesToDelete.includes(titreId) ? 4 : 2,
                            fillOpacity: titlesToDelete.includes(titreId) ? 0.6 : 0.2,
                            color: titlesToDelete.includes(titreId) ? "#dc3545" : "#3388ff",
                            fillColor: titlesToDelete.includes(titreId) ? "#dc3545" : "#3388ff",
                          });
                        }
                      },
                    }}
                  >
                    {/* Popup identique aux titres normaux */}
                    {!deleteMode && (
                      <Popup>
                        <div className="popup-modern">
                          {/* En-tête */}
                          <div className="popup-header">
                            <h3>{displayInfo.titref}</h3>
                            {feature.properties?.match_found && (
                              <span style={{
                                fontSize: '12px', 
                                background: '#4caf50',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '10px'
                              }}>
                                ✅ Vérifié
                              </span>
                            )}
                          </div>
                          
                          <div className="popup-section">
                            {displayInfo.surf_adop && (
                              <p>
                                <strong>Surface :</strong>{" "}
                                {Number(displayInfo.surf_adop).toFixed(2)} m²
                              </p>
                            )}
                            {displayInfo.pa_nom ? (
                              <p><strong>PA :</strong> {displayInfo.pa_nom}</p>
                            ) : (
                              <p className="popup-error">❌ Aucun PA trouvé</p>
                            )}
                          </div>

                          <div className="popup-section">
                            <h4>🟦 Zonages intersectés</h4>
                            {feature.properties?.match_found ? (
                              <p style={{color: '#4caf50', fontStyle: 'italic'}}>
                                
                              </p>
                            ) : (
                              <p className="popup-error">❌ Aucun zonage disponible pour les titres importés</p>
                            )}
                          </div>
                          
                          <div className="popup-highlight">
                            <strong>Surface constructible :</strong>{" "}
                            {displayInfo.surf_adop ? Number(displayInfo.surf_adop).toFixed(2) : "0.00"} m²
                          </div>

                          {/* Information supplémentaire pour les imports non trouvés */}
                          {!feature.properties?.match_found && (
                            <div style={{
                              marginTop: '10px',
                              padding: '8px',
                              background: '#fff3cd',
                              border: '1px solid #ffc107',
                              borderRadius: '5px',
                              fontSize: '12px'
                            }}>
                              <strong>ℹ️ Titre importé</strong>
                              <p style={{margin: '4px 0 0 0', fontSize: '11px'}}>
                                Fichier: <strong>{layer.name}</strong> ({layer.type})
                              </p>
                            </div>
                          )}
                        </div>
                      </Popup>
                    )}
                  </GeoJSON>
                );
              })
            )}


          {selectedZonage && zonagesDetails[selectedZonage.zonage_gid] && 
          zonagesDetails[selectedZonage.zonage_gid].polygones_individualises.map((polygone, idx) => {
            
            const isSelected = selectedPolygonFromZonagePanel && 
                              selectedPolygonFromZonagePanel.zonageGid === selectedZonage.zonage_gid && 
                              selectedPolygonFromZonagePanel.polygonIndex === idx;
            
            console.log(`🎯 Rendu polygone ${selectedZonage.zonage_gid}-${idx}: ${isSelected ? 'SELECTED' : 'normal'}`);
            
            return (
              <GeoJSON
                key={`${selectedZonage.zonage_gid}-${idx}-${isSelected ? 'selected' : 'normal'}`}
                data={JSON.parse(polygone.geom_geojson)}
                style={() => getZonagePolygonStyle(null, selectedZonage.zonage_gid, idx)}
                interactive={!selectionConfig.active}
                eventHandlers={{
                  click: (e) => {
                    if (selectionConfig.active && selectionConfig.disablePopups) {
                      console.log("🚫 Clic sur polygone zonage bloqué - Mode sélection actif");
                      e.originalEvent.stopPropagation();
                      e.originalEvent.preventDefault();
                      return;
                    }         
                    
                    console.log("🎯 Clic DIRECT sur polygone de zonage", { 
                      zonageGid: selectedZonage.zonage_gid, 
                      polygonIndex: idx 
                    });
                    
                    // 🔥 Bascule de sélection : si déjà sélectionné, on désélectionne
                    if (isSelected) {
                      handlePolygonSelectFromZonagePanel({ isSelected: false });
                    } else {
                      // Sinon, on sélectionne
                      handlePolygonSelectFromZonagePanel({
                        isSelected: true,
                        zonageGid: selectedZonage.zonage_gid,
                        polygonIndex: idx,
                        polygon: polygone
                      });
                    }
                    
                    e.target.closePopup();
                  }
                }}
              >
              {/* Popup amélioré avec état de sélection */}
              {!selectionConfig.active && (
                <Popup>
                  <div style={{ padding: "8px", minWidth: "200px" }}>
                    <div style={{ fontSize: "14px" }}>
                      <div><strong>Zonage:</strong> {selectedZonage.type_zonage}</div>
                      <div style={{ borderBottom: "1px solid #eee", margin: "4px 0" }}></div>
                      <div><strong>Polygone:</strong> #{idx + 1}</div>
                      <div><strong>Surface:</strong> {Number(polygone.surface_intersection_individuelle_m2).toFixed(2)} m²</div>
                      
                      {isSelected && (
                        <div style={{
                          background: "linear-gradient(135deg, #ff0000, #cc0000)",
                          color: "white",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          marginTop: "8px",
                          textAlign: "center",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                        }}>
                          ✓ Polygone sélectionné
                        </div>
                      )}
                      
                      <div style={{ marginTop: "12px", textAlign: "center" }}>
                        <button 
                          style={{
                            padding: "10px 16px",
                            background: isSelected 
                                      ? "linear-gradient(135deg, #ff6b6b, #ff0000)" 
                                      : "linear-gradient(135deg, #7dbae2, #2980b9)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                            transition: "all 0.3s ease",
                            width: "100%"
                          }}
                          onMouseOver={(e) => {
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow = "0 6px 8px rgba(0,0,0,0.15)";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                          }}
                          onClick={() => {
                            if (isSelected) {
                              handlePolygonSelectFromZonagePanel({ isSelected: false });
                            } else {
                              handlePolygonSelectFromZonagePanel({
                                isSelected: true,
                                zonageGid: selectedZonage.zonage_gid,
                                polygonIndex: idx,
                                polygon: polygone
                              });
                            }
                          }}
                        >
                          {isSelected ? "● Désélectionner" : "Sélectionner ce polygone"}
                        </button>
                      </div>
                      
                    </div>
                  </div>
                </Popup>
              )}
            </GeoJSON>
          );
        })
      }

        {searchPoint && (
          <Marker position={[searchPoint.y, searchPoint.x]} icon={searchIcon}>
            <Popup>
              Lat: {searchPoint.y.toFixed(6)}, Lng: {searchPoint.x.toFixed(6)}
            </Popup>
          </Marker>
        )}
         

        <MouseCoordinates setMouseCoords={setMouseCoords} />
    

       <DrawPolygonControl 
        setDrawnPolygon={setDrawnPolygon} 
        handlePointSearch={handlePointSearch}
        titresFromPolygonControl={titresFromPolygonControl}
        setTitresFromPolygonControl={setTitresFromPolygonControl}
        setDeleteModeGlobal={setDeleteMode}
        setTitlesToDeleteGlobal={setTitlesToDelete}
        deleteModeGlobal={deleteMode}
        titlesToDeleteGlobal={titlesToDelete}
        handleDeleteTitres={handleDeleteTitres}
        deletedTitlesRef={deletedTitlesRef}
        
      />
        {/* MODAL DE SUPPRESSION GLOBAL */}
        {deleteMode && (
        <div
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
           /* 🔹 CENTRAGE TOTAL */
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "white",
          padding: isMobile ? "8px" : "20px",
          borderRadius: isMobile ? "6px" : "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          zIndex: 10000,
          minWidth: isMobile ? "75%" : "600px",
          maxWidth: isMobile ? "90%" : "600px",
          maxHeight: isMobile ? "50vh" : "450px",
          overflowY: "auto",
          border: "2px solid #2980b9",
          transition: "all 0.3s ease",
          boxSizing: "border-box",

          overflowY: "auto",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch", 
        }}
      >
          
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
            borderBottom: "2px solid #2980b9",
            paddingBottom: "10px"
          }}>
            <h3 style={{ margin: 0, color: "#2980b9" }}>
              Sélectionnez les titres à supprimer 
            </h3>
            <button 
              onClick={() => setDeleteMode(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
                color: "#666",
                padding: "5px"
              }}
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>

          <div style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
            marginBottom: "15px"
          }}>
            <button
              onClick={() => {
                setTitlesToDelete(getAllDisplayedTitres().map(titre => titre.gid));
              }}
              style={{
                padding: "8px 16px",
                background: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                transition: "all 0.2s"
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#218838";
                e.target.style.transform = "scale(1.05)";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "#28a745";
                e.target.style.transform = "scale(1)";
              }}
            >
              ✓ Tout sélectionner
            </button>
            <button
              onClick={() => {
                setTitlesToDelete([]);
              }}
              style={{
                padding: "8px 16px",
                background: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                transition: "all 0.2s"
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#5a6268";
                e.target.style.transform = "scale(1.05)";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "#6c757d";
                e.target.style.transform = "scale(1)";
              }}
            >
              ✗ Tout désélectionner
            </button>
          </div>

          <div style={{ marginBottom: "15px" }}>
            {getAllDisplayedTitres().map(titre => (
              <div 
                key={titre.gid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px",
                  margin: "5px 0",
                  background: titlesToDelete.includes(titre.gid) ? "#e3f2fd" : "white",
                  border: `2px solid ${titlesToDelete.includes(titre.gid) ? "#2980b9" : "#ddd"}`,
                  borderRadius: "5px",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onClick={() => {
                  setTitlesToDelete(prev => 
                    prev.includes(titre.gid) 
                      ? prev.filter(id => id !== titre.gid)
                      : [...prev, titre.gid]
                  );
                }}
              >
                <input
                  type="checkbox"
                  checked={titlesToDelete.includes(titre.gid)}
                  onChange={() => {
                    setTitlesToDelete(prev => 
                      prev.includes(titre.gid) 
                        ? prev.filter(id => id !== titre.gid)
                        : [...prev, titre.gid]
                    );
                  }}
                  style={{ marginRight: "10px" }}
                />
                <div style={{ flex: 1 }}>
                  <strong>{titre.titref || titre.graphic_de || titre.num || "Titre sans nom"}</strong>
                  {titre.surf_adop && (
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      Surface: {Number(titre.surf_adop).toFixed(2)} m²
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
            marginTop: "15px",
            paddingTop: "15px",
            borderTop: "1px solid #eee"
          }}>
            <div style={{
              fontSize: "14px",
              color: "#666",
              fontWeight: "bold"
            }}>
              {titlesToDelete.length} titre(s) sélectionné(s)
            </div>
            
            <div style={{
              display: "flex",
              gap: "10px"
            }}>
              <button
                onClick={() => setDeleteMode(false)}
                style={{
                  padding: "8px 16px",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteTitres(titlesToDelete)}
                disabled={titlesToDelete.length === 0}
                style={{
                  padding: "8px 16px",
                  background: titlesToDelete.length === 0 ? "#ccc" : "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: titlesToDelete.length === 0 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontWeight: "bold"
                }}
              >
                <FaTrash size={12} />
                Supprimer ({titlesToDelete.length})
              </button>
            </div>
          </div>
        </div>
      )}

        <MeasureControl />

        

        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onFileSelect={handleFileSelect}
          onExport={handleExport}
        />

      <TitresDisplay
        titresData={getAllDisplayedTitres()} 
        selectedLayers={selectedLayers}
        setSelectedLayers={setSelectedLayers}
        zonagesChecked={zonagesChecked}
        setZonagesChecked={setZonagesChecked}
        zonagesDetails={zonagesDetails}
        setZonagesDetails={setZonagesDetails}
        polygonesExclus={polygonesExclus}
        setPolygonesExclus={setPolygonesExclus}
        selectedZonage={selectedZonage}
        setSelectedZonage={setSelectedZonage}
        deleteMode={deleteMode}
        titlesToDelete={titlesToDelete}
        setTitlesToDelete={setTitlesToDelete}
      />

      <ZonageCotationsPanel 
      selectedZonage={selectedZonage}
      onClose={() => {
        setSelectedZonage(null);
        setSelectedPolygonFromZonagePanel(null); 
      }}
      zonagesDetails={zonagesDetails}
      onSelectModeChange={handleSelectModeChange}
      onSideSelected={handleSideSelected}
      onPolygonSelect={handlePolygonSelectFromZonagePanel}
    />
      <TemporarySelectionPoints points={temporaryPoints} />
      <TemporarySelectionLine points={temporaryPoints} />

    <UnifiedControlPanel 
    toggleSearchPanel={toggleSearchPanel}
    isSearchPanelOpen={isSearchPanelOpen}
  />

      </MapContainer>

      <div className="mouse-coords">
        {mouseCoords
          ? `${mouseCoords.lat},${mouseCoords.lng}`
          : "Déplacez la souris "}
      </div>

      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Chargement des données</p>
        </div>
      )}
    </div>
  );
}